import React, { useEffect, useMemo, useRef } from 'react'

import { useCountUp } from 'react-countup'

import * as S from './styles'
import { Text } from '../text'

export type CountUpCardProps = {
  /**
   * Animated numerical value
   *
   * @example
   * ```tsx
   * <CountUpCard value={10} />
   * ```
   */
  value: number

  /**
   * Description of the card
   *
   * @example
   * ```tsx
   * <CountUpCard description="Description" />
   * ```
   */
  description: string

  /**
   * Value suffix
   *
   * @example
   * ```tsx
   * <CountUpCard suffix="/10" />
   * ```
   */
  suffix?: string | null

  /**
   * Value prefix
   *
   * @example
   * ```tsx
   * <CountUpCard prefix="+" />
   * ```
   */
  prefix?: string | null

  /**
   * Specifies if the component should start counting
   *
   * @example
   * ```tsx
   * <CountUpCard startCount={true} />
   * ```
   */
  startCount?: boolean
}

export const CountUpCard: React.FC<CountUpCardProps> = ({
  value,
  description,
  suffix,
  prefix,
  startCount = true,
}) => {
  const countUpRef = useRef<HTMLDivElement>(null)
  const doneCount = useRef(false)
  const precision = useMemo(() => String(value).split('.').length - 1, [value])

  const { start } = useCountUp({
    ref: countUpRef,
    start: 0,
    delay: 0.5,
    end: value,
    decimals: precision,
    duration: 3,
    startOnMount: false,
    useEasing: true,
    prefix: prefix || '',
    suffix: suffix || '',
  })

  // determines if the component should start counting up
  useEffect(() => {
    if (startCount && !doneCount.current) {
      doneCount.current = true
      start()
    }
  }, [startCount, start])

  useEffect(() => {
    doneCount.current = false
  }, [value])

  return (
    <S.CountUpCard>
      <Text size="h4" className="card-title">
        {/* the children div is updated by the useCountUp hook to display text */}
        <div ref={countUpRef} />
      </Text>
      <Text size="b3" className="card-description">
        {description}
      </Text>
    </S.CountUpCard>
  )
}
